import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { BarChart } from '@mui/x-charts/BarChart';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import CampaignPicker, { STATUS_LABEL } from './CampaignPicker.js';
import { smGet, errorText, personName } from './api.js';

const pct = x => `${Math.round((x || 0) * 100)}%`;

function Kpi({ label, value, sub }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Paper>
    );
}

function StatsTab({ campaignId, onCampaign }) {
    const [list, setList] = useState([]);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            setList(await smGet('/campaigns'));
            setData(campaignId ? await smGet(`/campaigns/${campaignId}`) : null);
        } catch (e) { setError(errorText(e)); }
    }, [campaignId]);
    useEffect(() => { load(); }, [load]);

    const stats = data?.stats;
    const campaign = data?.campaign;
    const contact = id => (campaign?.contacts || []).find(c => c.id === id) || {};
    const totals = list.reduce((t, c) => ({ contacted: t.contacted + c.stats.contacted, replied: t.replied + c.stats.replied, optedOut: t.optedOut + c.stats.optedOut, bounced: t.bounced + c.stats.bounced }), { contacted: 0, replied: 0, optedOut: 0, bounced: 0 });

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <CampaignPicker value={campaignId} onChange={onCampaign} allowAll />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!campaignId && (
                <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Kpi label="People contacted" value={totals.contacted} />
                        <Kpi label="Replied" value={totals.replied} sub={totals.contacted ? `${pct(totals.replied / totals.contacted)} of those contacted` : null} />
                        <Kpi label="Opted out" value={totals.optedOut} />
                        <Kpi label="Bounced" value={totals.bounced} />
                    </Box>
                    <Paper variant="outlined">
                        <Table size="small">
                            <TableHead><TableRow><TableCell>Campaign</TableCell><TableCell>Status</TableCell><TableCell align="right">People</TableCell><TableCell align="right">Contacted</TableCell><TableCell align="right">Replied</TableCell><TableCell align="right">Reply rate</TableCell><TableCell align="right">Opted out</TableCell><TableCell align="right">Bounced</TableCell></TableRow></TableHead>
                            <TableBody>
                                {list.map(c => (
                                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => onCampaign(c.id)}>
                                        <TableCell>{c.name}</TableCell>
                                        <TableCell>{STATUS_LABEL[c.status] || c.status}</TableCell>
                                        <TableCell align="right">{c.stats.people}</TableCell>
                                        <TableCell align="right">{c.stats.contacted}</TableCell>
                                        <TableCell align="right">{c.stats.replied}</TableCell>
                                        <TableCell align="right">{pct(c.stats.replyRate)}</TableCell>
                                        <TableCell align="right">{c.stats.optedOut}</TableCell>
                                        <TableCell align="right">{c.stats.bounced}</TableCell>
                                    </TableRow>
                                ))}
                                {!list.length && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No campaigns yet.</Typography></TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}

            {campaignId && stats && (
                <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Kpi label="People contacted" value={stats.contacted} sub={`of ${stats.approved} approved`} />
                        <Kpi label="Replied" value={stats.replied} sub={`${pct(stats.replyRate)} of those contacted`} />
                        <Kpi label="Interested or other replies" value={stats.positive} />
                        <Kpi label="Opted out" value={stats.optedOut} />
                        <Kpi label="Bounced" value={stats.bounced} />
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2">The funnel, by email in the sequence</Typography>
                        <Typography variant="caption" color="text.secondary">A reply counts for the last email the person had received before replying.</Typography>
                        <BarChart
                            height={280}
                            xAxis={[{ scaleType: 'band', data: stats.steps.map(s => s.label) }]}
                            series={[
                                { data: stats.steps.map(s => s.sent), label: 'Sent' },
                                { data: stats.steps.map(s => s.replied), label: 'Replied' },
                                { data: stats.steps.map(s => s.optedOut), label: 'Opted out' },
                            ]}
                        />
                        <Table size="small">
                            <TableHead><TableRow><TableCell>Email</TableCell><TableCell align="right">Sent</TableCell><TableCell align="right">Replied</TableCell><TableCell align="right">Reply rate</TableCell><TableCell align="right">Opted out</TableCell><TableCell align="right">Bounced</TableCell></TableRow></TableHead>
                            <TableBody>
                                {stats.steps.map(s => (
                                    <TableRow key={s.label}><TableCell>{s.label}</TableCell><TableCell align="right">{s.sent}</TableCell><TableCell align="right">{s.replied}</TableCell><TableCell align="right">{pct(s.replyRate)}</TableCell><TableCell align="right">{s.optedOut}</TableCell><TableCell align="right">{s.bounced}</TableCell></TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    <Paper variant="outlined">
                        <Typography variant="subtitle2" sx={{ p: 1.5 }}>Replies ({data.events.replies.length})</Typography>
                        <Table size="small">
                            <TableHead><TableRow><TableCell>When</TableCell><TableCell>From</TableCell><TableCell>Company</TableCell><TableCell>After</TableCell><TableCell>Reply</TableCell></TableRow></TableHead>
                            <TableBody>
                                {[...data.events.replies].reverse().map((r, i) => (
                                    <TableRow key={i} sx={{ verticalAlign: 'top' }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.at).format('D MMM, HH:mm')}</TableCell>
                                        <TableCell>{personName(contact(r.contactId))}<Typography variant="caption" display="block" color="text.secondary">{contact(r.contactId).title}</Typography></TableCell>
                                        <TableCell>{contact(r.contactId).company}</TableCell>
                                        <TableCell>{campaign.steps[r.step]?.label}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 520 }}>{r.optOut && <Chip size="small" label="Opted out" sx={{ mr: 1 }} />}{r.text}</TableCell>
                                    </TableRow>
                                ))}
                                {!data.events.replies.length && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No replies yet. The inbox is checked about every 5 minutes.</Typography></TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}
        </Box>
    );
}

export default StatsTab;
